import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getTransformer, getMeters, getFeeders, assignTransformerToFeeder, getTransformerDownstream, verifyMetersOnTransformer } from '../../api/topology.ts'
import { getTransformerSecondary } from '../../api/secondary.ts'
import type { GeoJSONFeatureCollection, GeoJSONPoint } from '../../types/topology.ts'
import { useTransformerMeasuredLoading, useAmiStatus } from '../../hooks/useAmiAnalytics.ts'
import { MEASURED_STATUS_COLORS } from '../../types/amiAnalytics.ts'
import type { MeasuredTransformerLoading, MeterWindowEntry } from '../../types/amiAnalytics.ts'
import type { LoadingBasis } from '../../types/engineering.ts'
import { StatusBadge } from './DetailPanel.tsx'
import { DownstreamButton } from './DownstreamButton.tsx'
import { HistorySection } from './HistorySection.tsx'
import { RelationshipBadge } from './RelationshipBadge.tsx'
import { VerificationProgressCard } from './VerificationProgressCard.tsx'
import { fmtPhase } from '../../utils/format.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { useAuth } from '../../hooks/useAuth.ts'
import { hasCapability, capabilityTooltip } from '../../utils/capabilities.ts'
import { useUndoWindow } from '../../hooks/useUndoWindow.ts'
import { isApiError } from '../../api/client.ts'

interface TransformerDetailProps {
  id: number
  onNavigate: (type: string, id: number) => void
  onShowDownstream: (fc: GeoJSONFeatureCollection<GeoJSONPoint>) => void
}

export function TransformerDetail({ id, onNavigate, onShowDownstream }: TransformerDetailProps) {
  const queryClient = useQueryClient()
  const [reassigning, setReassigning] = useState(false)
  const [reassignError, setReassignError] = useState<string | null>(null)
  const [showFeederPicker, setShowFeederPicker] = useState(false)

  // Feeder reassignment requires gis.override server-side (Phase 2A) —
  // disabled-with-tooltip, never hidden (UX C5, spec 3.12).
  const { user } = useAuth()
  const canOverride = hasCapability(user?.capabilities, 'gis.override')

  // 30 s undo after a successful feeder reassign (UX C4): the reverse is a
  // normal audited reassign. Only meaningful for gis.override holders (the
  // reassign UI itself is gated above). The slot carries the transformer it
  // was armed for (review fix: pin the undo to its entity — DetailPanel
  // re-renders this component keyless on navigation, so `id` alone cannot be
  // trusted as "the transformer the undo is for") plus BOTH feeders: the
  // previous one to restore, and the one we just assigned, which the reverse
  // write asserts is still current (409 otherwise).
  // Destructured (not used as `feederUndo.foo`) so `armFeederUndo`/`clearFeederUndo`
  // are plain stable identifiers — matches MapView's armUndo/clearUndo pattern
  // and keeps the reset-on-id-change effect's deps array exhaustive-deps-clean
  // (a `feederUndo.clear` member expression in a dep array cannot be verified
  // stable by the linter the way a destructured useCallback identity can).
  const {
    pending: feederUndoPending,
    arm: armFeederUndo,
    clear: clearFeederUndo,
  } = useUndoWindow<{
    transformerId: number
    previousFeederId: number
    assignedFeederId: number
  }>()

  // Switching which transformer this panel shows must drop any undo/error left
  // over from the previous one — otherwise a stale "Undo" could re-parent an
  // unrelated transformer once its reverse write fires (review finding,
  // Critical 1: id-switch was previously invisible to the undo slot).
  useEffect(() => {
    clearFeederUndo()
    setReassignError(null)
  }, [id, clearFeederUndo])

  const { data: transformer, isLoading } = useQuery({
    queryKey: ['transformer', id],
    queryFn: () => getTransformer(id),
  })

  const { data: meters } = useQuery({
    queryKey: ['transformer-meters', id],
    queryFn: () => getMeters({ transformer_id: id, limit: 50 }),
    enabled: !!transformer,
  })

  // AMI-measured loading is the default basis; the 5 kW/meter model is only a
  // labeled fallback when AMI has no data or coverage is below threshold.
  const { data: measured, isLoading: measuredLoading } =
    useTransformerMeasuredLoading(id, !!transformer)
  const { data: amiStatus } = useAmiStatus()

  const { data: feeders } = useQuery({
    queryKey: ['feeders-list'],
    queryFn: () => getFeeders({ limit: 200 }),
    enabled: showFeederPicker,
  })

  // Derived secondary network (connected segments + meters via the secondary
  // graph). Degrades to available:false when no published run covers the feeder.
  const { data: secondary } = useQuery({
    queryKey: ['transformer-secondary', id],
    queryFn: () => getTransformerSecondary(id),
    enabled: !!transformer,
  })

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading transformer...</div>
  }

  if (!transformer) {
    return <div className="p-4 text-sm text-red-400">Transformer not found</div>
  }

  async function handleAssignFeeder(feederId: number) {
    setReassigning(true)
    setReassignError(null)
    // Capture the prior feeder BEFORE the write so the undo can reverse it.
    const previousFeederId = transformer?.feeder_id ?? null
    try {
      await assignTransformerToFeeder(id, feederId)
      await queryClient.invalidateQueries({ queryKey: ['transformer', id] })
      await queryClient.invalidateQueries({ queryKey: ['map'] })
      // The transformer just gained a feeder — drop it from the global unlinked
      // work queue so the VerificationProgressCard count stays current.
      await queryClient.invalidateQueries({ queryKey: ['unlinked-transformers'] })
      setShowFeederPicker(false)
      if (previousFeederId != null && previousFeederId !== feederId) {
        armFeederUndo({ transformerId: id, previousFeederId, assignedFeederId: feederId })
      } else {
        // First-time assignment (nothing to reverse) or a no-op re-pick.
        clearFeederUndo()
      }
    } catch (err) {
      // Task 8.9 (A8) converted this file to friendlyError — keep it.
      setReassignError(friendlyError(err, 'assign the feeder'))
    } finally {
      setReassigning(false)
    }
  }

  async function handleUndoFeederReassign() {
    const pending = feederUndoPending
    if (!pending) return
    if (pending.transformerId !== id) {
      // Stale undo for a transformer other than the one currently displayed.
      // The id-change effect above should already have cleared this, but
      // refuse here too rather than ever reverse-assigning the wrong row
      // (review Critical 1 — belt and suspenders).
      clearFeederUndo()
      return
    }
    // NOTE: do NOT clear the undo before awaiting. Clearing first is what made
    // a failed undo invisible — the banner vanished and the error had nowhere
    // to land, so the operator read silence as success.
    setReassigning(true)
    setReassignError(null)
    try {
      await assignTransformerToFeeder(pending.transformerId, pending.previousFeederId, {
        // Only reverse if the transformer is still where we just put it.
        expectedCurrentFeederId: pending.assignedFeederId,
      })
      await queryClient.invalidateQueries({ queryKey: ['transformer', pending.transformerId] })
      await queryClient.invalidateQueries({ queryKey: ['map'] })
      await queryClient.invalidateQueries({ queryKey: ['unlinked-transformers'] })
      clearFeederUndo()
    } catch (err) {
      // Drop the banner (the offer is spent) but make the failure loud in the
      // slot the banner occupied — see the unconditional error render below.
      clearFeederUndo()
      setReassignError(
        isApiError(err) && err.status === 409
          ? 'Someone else changed this since — refresh to see the current assignment.'
          : friendlyError(err, 'undo the reassignment'),
      )
    } finally {
      setReassigning(false)
    }
  }

  // Loading basis (spec §3.4). Prefer the server's loading_basis; fall back to
  // the pre-hybrid heuristic for older payloads that predate the field so the
  // measured/model split still resolves.
  const basis: LoadingBasis =
    measured?.loading_basis ??
    (measured &&
    measured.loading_status !== 'insufficient_data' &&
    measured.max_loading_pct != null
      ? 'measured'
      : 'model')
  const hasMeasured = !!measured && basis !== 'model'
  const modelNote = measuredLoading
    ? undefined
    : measured && measured.loading_status === 'insufficient_data'
      ? 'AMI coverage below threshold — showing model estimate'
      : 'No AMI data yet — showing model estimate'

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="text-base font-semibold text-gray-900">
          {transformer.name || transformer.gis_id || `Transformer #${transformer.id}`}
        </div>
        {transformer.address && (
          <div className="text-sm text-gray-500">{transformer.address}</div>
        )}
      </div>

      {/* Status */}
      <StatusBadge status={transformer.verification_status} />

      {/* Key details */}
      <div className="space-y-1.5 text-sm">
        <DetailRow label="GIS ID" value={transformer.gis_id} />
        <DetailRow label="KVA Rating" value={transformer.kva_rating} />
        <DetailRow label="Phase" value={transformer.phase == null ? null : fmtPhase(transformer.phase)} />
        <DetailRow label="Primary V" value={transformer.voltage_primary} />
        <DetailRow label="Secondary V" value={transformer.voltage_secondary} />
      </div>

      {/* Loading — AMI-measured by default, model estimate as labeled fallback */}
      {transformer.kva_rating && transformer.kva_rating > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Loading</p>
            <BasisTag basis={basis} />
          </div>
          {hasMeasured && amiStatus?.is_stale && (
            <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              AMI data is stale (as of {amiStatus.as_of}, older than the{' '}
              {amiStatus.staleness_hours_threshold}h threshold) — measured
              loading may not reflect current conditions.
            </div>
          )}
          {hasMeasured ? (
            <MeasuredLoading m={measured!} asOf={amiStatus?.as_of ?? null} basis={basis} />
          ) : (
            <LoadingEstimate
              kvaRating={transformer.kva_rating}
              meterCount={transformer.meter_count ?? 0}
              note={modelNote}
            />
          )}
        </div>
      )}

      {/* Per-meter measured (30d) — what each meter measured. Independent of the
          bank's kVA rating, so it renders even when the Loading card is hidden. */}
      {measured?.meters && measured.meters.length > 0 && (
        <PerMeterMeasured meters={measured.meters} onNavigate={onNavigate} />
      )}

      {/* Parent feeder */}
      <div className="border-t border-gray-200 pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Parent Feeder</p>
          <button
            onClick={() => setShowFeederPicker(!showFeederPicker)}
            disabled={!canOverride}
            title={!canOverride ? capabilityTooltip('gis.override') : undefined}
            className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {showFeederPicker ? 'Cancel' : transformer.feeder ? 'Reassign' : 'Assign'}
          </button>
        </div>

        {/* Reassign/undo failures must be visible whether or not the picker is
            open — the picker is closed by the time an undo can fail. */}
        {reassignError && (
          <p className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
            {reassignError}
          </p>
        )}

        {showFeederPicker ? (
          <div className="space-y-1.5">
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
              {feeders ? (
                feeders.map(f => (
                  <button
                    key={f.id}
                    onClick={() => handleAssignFeeder(f.id)}
                    disabled={reassigning || f.id === transformer.feeder_id}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-40 border-b border-gray-100 last:border-0"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    <span className="text-gray-700 truncate flex-1">
                      {f.name || f.code || `Feeder #${f.id}`}
                    </span>
                    {f.id === transformer.feeder_id && (
                      <span className="text-xs text-gray-400">current</span>
                    )}
                  </button>
                ))
              ) : (
                <div className="p-2 text-sm text-gray-400">Loading feeders...</div>
              )}
            </div>
          </div>
        ) : transformer.feeder ? (
          <button
            onClick={() => onNavigate('Feeder', transformer.feeder!.id)}
            className="w-full text-left p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-sm font-medium text-gray-700">
                {transformer.feeder.name || transformer.feeder.code || `Feeder #${transformer.feeder.id}`}
              </span>
              <RelationshipBadge source={(transformer as unknown as Record<string, unknown>).relationship_source as string} />
            </div>
          </button>
        ) : (
          <div className="p-2 rounded bg-orange-50 border border-orange-200 text-sm text-orange-700">
            No feeder linked
          </div>
        )}

        {canOverride && feederUndoPending && (
          <div className="mt-2 flex items-center justify-between text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
            <span className="text-blue-700">Feeder reassigned.</span>
            <button
              onClick={handleUndoFeederReassign}
              disabled={reassigning}
              className="px-2 py-0.5 font-medium text-blue-700 underline hover:no-underline disabled:opacity-50"
            >
              Undo
            </button>
          </div>
        )}
      </div>

      {/* Connected meters */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Connected Meters ({transformer.meter_count})
        </p>
        {meters && meters.length > 0 ? (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {meters.map(m => (
              <button
                key={m.id}
                onClick={() => onNavigate('Meter', m.id)}
                className="w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-gray-700 truncate flex-1">
                  {m.meter_number || `#${m.id}`}
                </span>
                <span className="text-xs text-gray-400 truncate max-w-[40%]">
                  {m.service_address}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400">No meters connected</div>
        )}

        {/* Bulk "verify all meters on this transformer" (item 4) */}
        {transformer.meter_count > 0 && (
          <div className="mt-2">
            <VerifyAllMetersButton transformerId={id} meterCount={transformer.meter_count} />
          </div>
        )}
      </div>

      {/* Feeder verification progress + unlinked work queue (item 4) */}
      {transformer.feeder_id != null && (
        <div className="border-t border-gray-200 pt-3">
          <VerificationProgressCard feederId={transformer.feeder_id} />
        </div>
      )}

      {/* Secondary network (derived graph) */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Secondary Network
        </p>
        {!secondary || !secondary.available ? (
          <div className="text-sm text-gray-400">Not computed</div>
        ) : (
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Secondary Segments</span>
              <span className="text-gray-800 font-medium">{secondary.secondary_segment_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Connected Meters (secondary)</span>
              <span className="text-gray-800 font-medium">{secondary.meter_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Components</span>
              <span className="text-gray-800 font-medium">{secondary.component_ids.length}</span>
            </div>
          </div>
        )}
      </div>

      {/* Audit history (UX C4) */}
      <HistorySection entityType="transformer" id={id} />

      {/* Downstream members highlight (spec §4.3 "show everything fed by …") */}
      <div className="border-t border-gray-200 pt-3">
        <DownstreamButton
          // Keyed by transformer so switching transformers resets the
          // count/notice state (same shared component the device panel uses).
          key={id}
          label="Show everything fed by this transformer"
          fetcher={() => getTransformerDownstream(id)}
          onShowDownstream={onShowDownstream}
        />
      </div>
    </div>
  )
}

/** "Verify all meters on this transformer" — bulk verify with a confirm and
 *  query invalidation so the connected-meter statuses, map, and data-quality
 *  rollup all reflect the change (item 4). */
function VerifyAllMetersButton({
  transformerId,
  meterCount,
}: {
  transformerId: number
  meterCount: number
}) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  // Bulk sweep — requires topology.verify.bulk server-side (UX C5, spec 3.12).
  const { user } = useAuth()
  const canBulkVerify = hasCapability(user?.capabilities, 'topology.verify.bulk')

  async function handleClick() {
    if (!window.confirm(`Verify all ${meterCount} meter(s) attached to this transformer?`)) return
    setBusy(true)
    setErr(null)
    setMsg(null)
    try {
      const res = await verifyMetersOnTransformer(transformerId, 'verified')
      await queryClient.invalidateQueries({ queryKey: ['transformer', transformerId] })
      await queryClient.invalidateQueries({ queryKey: ['transformer-meters', transformerId] })
      await queryClient.invalidateQueries({ queryKey: ['map'] })
      await queryClient.invalidateQueries({ queryKey: ['data-quality'] })
      // Bulk-verifying meters moves this feeder's verification bars — refresh
      // any mounted VerificationProgressCard.
      await queryClient.invalidateQueries({ queryKey: ['feeder-verification'] })
      setMsg(`Verified ${res.verified} meter(s).`)
    } catch (e) {
      setErr(friendlyError(e, 'verify the meters'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <button
        onClick={handleClick}
        disabled={busy || !canBulkVerify}
        title={!canBulkVerify ? capabilityTooltip('topology.verify.bulk') : undefined}
        className="w-full px-3 py-1.5 text-xs font-medium border rounded transition-colors bg-green-50 text-green-700 border-green-200 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Verifying…' : `Verify all ${meterCount} meters on this transformer`}
      </button>
      {msg && <p className="text-xs text-green-600">{msg}</p>}
      {err && <p className="text-xs text-red-500">{err}</p>}
    </div>
  )
}

function LoadingEstimate({ kvaRating, meterCount, note }: { kvaRating: number; meterCount: number; note?: string }) {
  const avgDemandKw = 5.0
  const estimatedKw = meterCount * avgDemandKw
  const loadingPct = kvaRating > 0 ? (estimatedKw / kvaRating) * 100 : 0

  let status: string
  let color: string
  let bgColor: string
  if (loadingPct > 100) {
    status = 'Overloaded'
    color = '#e74c3c'
    bgColor = '#fef2f2'
  } else if (loadingPct >= 70) {
    status = 'Heavy'
    color = '#f1c40f'
    bgColor = '#fefce8'
  } else if (loadingPct >= 20) {
    status = 'Normal'
    color = '#2ecc71'
    bgColor = '#f0fdf4'
  } else {
    status = 'Underloaded'
    color = '#3498db'
    bgColor = '#eff6ff'
  }

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-gray-500">Est. Load ({meterCount} meters x {avgDemandKw} kW)</span>
        <span className="font-medium text-gray-800">{estimatedKw.toFixed(1)} kW</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(loadingPct, 100)}%`, backgroundColor: color }}
          />
        </div>
        <span className="text-sm font-medium" style={{ color }}>{loadingPct.toFixed(1)}%</span>
      </div>
      <div className="flex justify-end">
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ backgroundColor: bgColor, color }}
        >
          {status}
        </span>
      </div>
      {note && <div className="text-[11px] text-gray-400">{note}</div>}
    </div>
  )
}

/** Soft chip backgrounds keyed to the measured loading status. */
const MEASURED_STATUS_BG: Record<string, string> = {
  overloaded: '#fef2f2',
  heavy: '#fefce8',
  normal: '#f0fdf4',
  underloaded: '#eff6ff',
  no_rating: '#f3f4f6',
  insufficient_data: '#f5f3ff',
}

function BasisTag({ basis }: { basis: LoadingBasis }) {
  if (basis === 'hybrid') {
    // Measured coincident peak plus a flat estimate for each silent meter.
    return (
      <span
        className="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: '#fffbeb', color: '#b45309' }}
        title="Measured coincident peak + a flat estimate for each silent (non-reporting) meter"
      >
        Measured + est.
      </span>
    )
  }
  if (basis === 'measured') {
    return (
      <span
        className="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: '#f5f3ff', color: '#7c3aed' }}
      >
        Measured
      </span>
    )
  }
  return (
    <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
      Model estimate
    </span>
  )
}

/** "Per-meter measured (30d)" — lists each active meter's trailing-window OWN
 *  (non-coincident) peak kW and its COINCIDENT contribution at the bank's peak
 *  hour, in two right-aligned columns. Silent meters (no AMI data) are amber
 *  with a dash in both columns and the stale-GIS note. Clicking a row selects
 *  that meter via the SAME onNavigate handler the Connected Meters list uses, so
 *  the map pan/highlight fires. */
function PerMeterMeasured({
  meters,
  onNavigate,
}: {
  meters: MeterWindowEntry[]
  onNavigate: (type: string, id: number) => void
}) {
  const anySilent = meters.some(m => m.silent)
  // Every matched meter on a bank shares the SAME coincident peak hour; take the
  // first present one to caption the section (also gates the bank-peak line off
  // when no transformer_load_daily row covered the window).
  const bankPeakAt = meters.find(m => m.bank_peak_at != null)?.bank_peak_at ?? null
  // NULL (missing) renders as an em dash; a real 0.0 shows "0.0 kW".
  const fmtKw = (kw: number | null | undefined) =>
    kw != null ? `${kw.toFixed(1)} kW` : '—'

  return (
    <div className="border-t border-gray-200 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Per-meter measured (30d)
      </p>
      {/* Column header: OWN (non-coincident) peak vs coincident at-bank-peak. */}
      <div className="flex items-center gap-2 px-2 mb-1 text-[10px] uppercase tracking-wider text-gray-400">
        <span className="w-1.5" />
        <span className="flex-1" />
        <span className="w-16 text-right">own peak</span>
        <span className="w-20 text-right">at bank peak</span>
      </div>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {meters.map(m => (
          <button
            key={m.meter_id}
            onClick={() => onNavigate('Meter', m.meter_id)}
            className={`w-full text-left px-2 py-1 rounded text-sm transition-colors flex items-center gap-2 ${
              m.silent ? 'hover:bg-amber-50' : 'hover:bg-gray-50'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                m.silent ? 'bg-amber-400' : 'bg-gray-400'
              }`}
            />
            <span
              className={`truncate flex-1 ${
                m.silent ? 'text-amber-700' : 'text-gray-700'
              }`}
            >
              {m.meter_number || `#${m.meter_id}`}
            </span>
            {/* Own (non-coincident) peak — silent meters dash both columns. */}
            <span
              className={`w-16 text-right text-xs font-medium tabular-nums ${
                m.silent ? 'text-amber-600' : 'text-gray-800'
              }`}
            >
              {m.silent ? '—' : fmtKw(m.peak_kw)}
            </span>
            {/* Coincident contribution at the bank's measured peak hour. */}
            <span
              className={`w-20 text-right text-xs font-medium tabular-nums ${
                m.silent ? 'text-amber-600' : 'text-gray-800'
              }`}
            >
              {m.silent ? '—' : fmtKw(m.kw_at_bank_peak)}
            </span>
          </button>
        ))}
      </div>
      {anySilent && (
        <div className="mt-1 text-[11px] text-amber-700">
          Amber meters have no AMI data — possibly a stale GIS meter record.
        </div>
      )}
      {/* Two-part caption. The own-peak non-coincidence caveat always stays; when
          a coincident bank-peak hour is available, add the (softened) reassurance
          about the "at bank peak" column. "≈ / as of the peak day's meter set"
          because renames, reassignments, and alias differences between the peak
          day and the window rollup can shift the meter set slightly (review
          finding on 1090bf4). bank_peak_at is UTC — render browser-local. */}
      <div className="mt-1 text-[11px] text-gray-400">
        Individual meter peaks are not coincident — they will not sum to the bank peak.
      </div>
      {bankPeakAt && (
        <div className="mt-1 text-[11px] text-gray-500">
          At bank peak ({new Date(bankPeakAt).toLocaleString()}): contributions
          sum to ≈ the bank's measured peak (as of the peak day's meter set).
        </div>
      )}
    </div>
  )
}

function MeasuredLoading({
  m,
  asOf,
  basis,
}: {
  m: MeasuredTransformerLoading
  asOf: string | null
  basis: LoadingBasis
}) {
  const isHybrid = basis === 'hybrid'
  // Hybrid colors the bar/number by the effective (measured + est.) load;
  // pure-measured keeps the measured max.
  const pct = (isHybrid ? m.effective_loading_pct : m.max_loading_pct) ?? 0
  const color = MEASURED_STATUS_COLORS[m.loading_status] ?? '#9ca3af'
  const bgColor = MEASURED_STATUS_BG[m.loading_status] ?? '#f3f4f6'
  const statusLabel = m.loading_status.replace(/_/g, ' ')
  const silent = m.meters_missing
  return (
    <div className="space-y-1.5">
      {isHybrid ? (
        <>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Effective peak (measured + est.)</span>
            <span className="font-medium text-gray-800">
              {m.effective_peak_kw != null ? `${m.effective_peak_kw.toFixed(1)} kW` : '—'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-sm font-medium" style={{ color }}>{pct.toFixed(1)}%</span>
          </div>
          {/* How the effective peak was built: measured coincident peak plus a
              flat avg-demand estimate for each silent (non-reporting) meter. */}
          <div className="text-[11px] text-gray-500">
            Measured peak {m.peak_kw_max != null ? `${m.peak_kw_max.toFixed(1)} kW` : '—'} (
            {m.meters_reporting} of {m.meter_count} meters) +{' '}
            {m.estimated_missing_kw.toFixed(1)} kW est. ({silent} silent meter
            {silent === 1 ? '' : 's'})
          </div>
          {/* Silent meters double as a GIS-cleanup worklist. */}
          {m.silent_meter_numbers && m.silent_meter_numbers.length > 0 && (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-1">
                {m.silent_meter_numbers.map(mn => (
                  <span
                    key={mn}
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                    title="No AMI data — possibly a stale GIS meter record"
                  >
                    {mn}
                  </span>
                ))}
              </div>
              <div className="text-[11px] text-amber-700">
                No AMI data — possibly a stale GIS meter record
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">
              Measured peak ({m.meters_reporting}/{m.meter_count} meters)
            </span>
            <span className="font-medium text-gray-800">
              {m.peak_kw_max != null ? `${m.peak_kw_max.toFixed(1)} kW` : '—'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-sm font-medium" style={{ color }}>{pct.toFixed(1)}%</span>
          </div>
          {/* Director UX (2026-07): once we have a measured value the standalone
              model estimate below it adds noise, so it is dropped here. The
              hybrid breakdown line keeps its "+ N kW est." term because that
              explains how the effective peak was built. */}
        </>
      )}
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full capitalize"
          style={{ backgroundColor: bgColor, color }}
        >
          {statusLabel}
        </span>
        <span className="text-[11px] text-gray-400 text-right">
          30-day peak · {m.window_coverage_pct}% coverage{asOf ? ` · as of ${asOf}` : ''}
        </span>
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
